import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../../components/AdminLayout';
import { Card } from '../../styles/GlobalStyles';
import { clubService } from '../../services/clubService';
import { ClubDashboardStats, SubscriptionStatus } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const WelcomeCard = styled(Card)`
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  background: linear-gradient(135deg, var(--primary-color)15 0%, var(--primary-color)05 100%);
`;

const WelcomeTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-sm);
`;

const WelcomeText = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
`;

const SubscriptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const SubscriptionStatusText = styled.div<{ status: string }>`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => {
    switch (props.status) {
      case 'ACTIVE': return 'var(--success-color, #28a745)';
      case 'INACTIVE': return 'var(--text-secondary)';
      case 'CANCELLED': return 'var(--error-color, #dc3545)';
      case 'PAST_DUE': return 'var(--warning-color, #ffc107)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const TeamLimitText = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--error-bg, #fee);
  color: var(--error-color, #c00);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
`;

export const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ClubDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTimeOfDayGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatSubscriptionStatus = (status: SubscriptionStatus | null): string => {
    if (!status) return 'Not Active';
    switch (status) {
      case SubscriptionStatus.ACTIVE: return 'Active';
      case SubscriptionStatus.INACTIVE: return 'Inactive';
      case SubscriptionStatus.CANCELLED: return 'Cancelled';
      case SubscriptionStatus.PAST_DUE: return 'Past Due';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.profile?.clubId) {
        setError('No club associated with your account');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const dashboardStats = await clubService.getDashboardStats(user.profile.clubId);
        setStats(dashboardStats);
      } catch (err: any) {
        console.error('Failed to fetch dashboard stats:', err);
        setError(err.response?.data?.error?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.profile?.clubId]);

  if (loading) {
    return (
      <AdminLayout pageTitle="Dashboard">
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Dashboard">
      <WelcomeCard>
        <WelcomeTitle>
          {getTimeOfDayGreeting()}, {user?.profile?.firstName || 'Admin'}
        </WelcomeTitle>
        <WelcomeText>
          Welcome to your club administration dashboard. Here's an overview of your club.
        </WelcomeText>
      </WelcomeCard>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {stats && (
        <DashboardGrid>
          <StatCard>
            <StatLabel>Total Teams</StatLabel>
            <StatValue>{stats.totalTeams}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Total Coaches</StatLabel>
            <StatValue>{stats.totalCoaches}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Total Players</StatLabel>
            <StatValue>{stats.totalPlayers}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Subscription Status</StatLabel>
            <SubscriptionInfo>
              <SubscriptionStatusText status={stats.subscriptionStatus || 'INACTIVE'}>
                {formatSubscriptionStatus(stats.subscriptionStatus)}
              </SubscriptionStatusText>
              {stats.subscriptionStatus && (
                <TeamLimitText>
                  {stats.totalTeams} of {stats.teamLimit} teams used
                </TeamLimitText>
              )}
            </SubscriptionInfo>
          </StatCard>
        </DashboardGrid>
      )}
    </AdminLayout>
  );
};
